import type { AAPlugin } from '@particle-network/aa-plugin';
import type { Connection } from '@particle-network/connector-core';
import type { Plugin } from '@particle-network/plugin-core';
import type { WalletPlugin } from '@particle-network/wallet-plugin';
import React, { createContext, useEffect, useMemo, useRef } from 'react';
import { useContext } from '..';
import { useAAPlugin } from '../../hooks/useAAPlugin';
import { useAccount } from '../../hooks/useAccount';
import { useAppearance } from '../../hooks/useAppearance';
import { useConnections } from '../../hooks/useConnections';
import { getWalletOption } from '../../styles';
import { hydrate } from '../../utils/hydrate';

type ContextValue = {
  foo?: string;
};

export const ModalContext = createContext<ContextValue | null>(null);

const modalContextValue: ContextValue = {};

type Props = {
  reconnectOnMount: boolean;
};

let reconnectConfigUid;

/**
 * manage modal status
 */
export const ModalProvider = (props: React.PropsWithChildren<Props>) => {
  const { reconnectOnMount } = props;

  const { config } = useContext();

  const connections = useConnections();

  const account = useAccount();

  const { appearance } = useAppearance();

  const setSmartAccount = useAAPlugin((state) => state.setSmartAccount);

  const handlePlugin = async (connections: Connection[], plugins: Plugin[]) => {
    const result = connections.filter((connection) => connection.connector && connection.connector.getProvider);
    for (const plugin of plugins) {
      if (result.length) {
        console.log('Plugin onConnect----id:', plugin.id);
        await plugin.onConnect?.(result as [Connection]);
      } else {
        console.log('Plugin onDisconnect----id:', plugin.id);
        await plugin.onDisconnect?.();
      }
    }
    const aaPlugin = config.plugins.find((plugin) => plugin.id === 'aa') as AAPlugin;
    setSmartAccount(aaPlugin?.smartAccount ?? null);
  };

  useEffect(() => {
    console.log(
      `account changed --- status: ${account.status}, address: ${account.address}, 
      chainId: ${account.chainId}, isConnected:${account.isConnected}, isConnecting:${account.isConnecting}, 
      isDisconnected:${account.isDisconnected}, connectorId: ${account.connector?.id}`
    );
  }, [account]);

  // --- reconnect
  const reconnectRef = useRef(false);
  const { onMount } = hydrate(config, {
    reconnectOnMount,
  });
  if (!reconnectRef.current) {
    reconnectRef.current = true;
    reconnectConfigUid = config.uid;
    onMount();
  }

  useEffect(() => {
    if (config.uid !== reconnectConfigUid) {
      reconnectConfigUid = config.uid;
      onMount();
    }
  }, [config.uid, onMount]);

  const walletPlugin = useMemo(() => {
    const plugin = config.plugins.find((plugin) => plugin.id === 'wallet');
    if (plugin) {
      return plugin as WalletPlugin;
    }
    return undefined;
  }, [config.plugins]);

  useEffect(() => {
    if (walletPlugin) {
      console.log('Plugin wallet init');
      const aaPlugin = config.plugins.find((plugin) => plugin.id === 'aa') as AAPlugin;
      const erc4337 = aaPlugin ? aaPlugin.erc4337 : undefined;
      walletPlugin.init({
        erc4337,
      });
    }
  }, [walletPlugin, config.plugins]);

  useEffect(() => {
    if (walletPlugin) {
      const walletOption = getWalletOption(appearance);
      walletPlugin.overrideWalletOption({
        ...walletOption,
        language: appearance.language,
      });
    }
  }, [appearance, walletPlugin]);

  useEffect(() => {
    handlePlugin(connections, config.plugins);
  }, [connections, config.plugins]);

  return <ModalContext.Provider value={modalContextValue}>{props.children}</ModalContext.Provider>;
};

export const useModalContext = () => {
  const context = React.useContext(ModalContext);
  if (!context) throw Error('Modal Hook must be inside a Provider.');
  return context;
};
